﻿// packages/shared/src/services/authService.ts - Complete implementation with email preferences and debugging logs
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  GoogleAuthProvider,
  linkWithPopup,
  unlink
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FirebaseService } from './firebaseService';
import { User, LinkedAccount, PlayerRatings, EmailPreferences } from '../types';

export class AuthService {
  private static instance: AuthService;
  private firebaseService: FirebaseService;
  private googleProvider: GoogleAuthProvider;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope('email');
    this.googleProvider.addScope('profile');
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async signIn(email: string, password: string): Promise<User> {
    return this.login(email, password);
  }

  public async signUp(email: string, password: string, name: string): Promise<User> {
    return this.register(email, password, name);
  }

  public async register(email: string, password: string, name: string): Promise<User> {
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create initial ratings as 'Not Rated'
    const initialRatings: PlayerRatings = {
      utr: null,
      ntrp: null
    };

    // Default email preferences - all enabled by default
    const defaultEmailPreferences: EmailPreferences = {
      challengeReceived: true,
      challengeAccepted: true,
      challengeRejected: true,
      accountSecurity: true
    };
    
    const userData: any = {
      email,
      name,
      emailVerified: false, // Starts as false
      preferences: {
        level: 'Not Rated',
        ratings: initialRatings,
        preferredCourts: [],
        defaultDuration: 60,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        emailNotifications: true,
        emailPreferences: defaultEmailPreferences // Add email preferences
      },
      linkedAccounts: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Only add profilePicture if it has a value
    if (userCredential.user.photoURL) {
      userData.profilePicture = userCredential.user.photoURL;
    }

    // THIS TRIGGERS THE EMAIL AUTOMATICALLY via onUserCreated trigger
    await setDoc(doc(db, 'users', userCredential.user.uid), userData);

    // SIGN OUT USER IMMEDIATELY - They can't stay logged in until verified
    await auth.signOut();

    return { uid: userCredential.user.uid, ...userData } as User;
  }

  public async login(email: string, password: string): Promise<User> {
    console.log('🔍 Starting login for email:', email);
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ Firebase authentication successful');
    
    // Get user document from Firestore FIRST to check our custom verification
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    
    if (!userDoc.exists()) {
      console.error('❌ User document not found in Firestore');
      await auth.signOut();
      throw new Error('User data not found');
    }

    const userData = userDoc.data();
    console.log('📄 User document found:', {
      hasEmailVerified: userData.emailVerified,
      hasPreferences: !!userData.preferences,
      hasEmailPreferences: !!userData.preferences?.emailPreferences,
      emailNotifications: userData.preferences?.emailNotifications
    });
    
    if (!userData.emailVerified) {
      console.log('❌ Email not verified');
      await auth.signOut();
      const error = new Error('Please verify your email before signing in. Check your inbox for the verification link.');
      (error as any).code = 'auth/email-not-verified';
      throw error;
    }

    // Simple fix: provide defaults for missing fields
    const preferences = userData.preferences || {};
    const emailPreferences = preferences.emailPreferences || {
      challengeReceived: true,
      challengeAccepted: true,
      challengeRejected: true,
      accountSecurity: true
    };

    console.log('✅ Login successful, returning user data');
    return { 
      uid: userCredential.user.uid, 
      ...userData,
      preferences: {
        ...preferences,
        emailPreferences: emailPreferences,
        emailNotifications: preferences.emailNotifications !== false
      },
      createdAt: (userData.createdAt as any)?.toDate ? (userData.createdAt as any).toDate() : userData.createdAt,
      updatedAt: (userData.updatedAt as any)?.toDate ? (userData.updatedAt as any).toDate() : userData.updatedAt
    } as User;
  }

  public async signInWithGoogle(): Promise<User> {
    console.log('🔍 Starting signInWithGoogle...');
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();

    const result = await signInWithPopup(auth, this.googleProvider);
    const firebaseUser = result.user;
    console.log('🔍 Firebase user:', firebaseUser);

    if (!firebaseUser.email) {
      throw new Error('Google account must have an email address');
    }

    // Check if user already exists by email
    console.log('🔍 Checking for existing user with email:', firebaseUser.email);
    const existingUserQuery = query(
      collection(db, 'users'),
      where('email', '==', firebaseUser.email)
    );
    const existingUsers = await getDocs(existingUserQuery);
    console.log('🔍 Existing users found:', existingUsers.size);

    let userData: User;

if (!existingUsers.empty) {
  // User exists - check what needs updating
  const existingUserDoc = existingUsers.docs[0];
  const existingUserData = existingUserDoc.data() as Omit<User, 'uid'>;
  
  console.log('🔍 Existing user found, checking for updates needed');
  
  // Check if Google account is already linked
  const linkedAccounts = Array.isArray(existingUserData.linkedAccounts) 
    ? existingUserData.linkedAccounts 
    : [];
  
  const googleLinked = linkedAccounts.find((account: any) => account.providerId === 'google.com');
  const needsGoogleLink = !googleLinked;
  
  // Check if profile picture needs updating
  const needsProfilePictureUpdate = firebaseUser.photoURL && !existingUserData.profilePicture;

  // Only update document if there are actual changes needed
  if (needsGoogleLink || needsProfilePictureUpdate) {
    console.log('🔍 Updates needed, preparing updateData...');
    
    const updateData: any = {
      updatedAt: serverTimestamp()
    };

    // Add Google account to linked accounts if not already linked
    if (needsGoogleLink) {
      const newLinkedAccount: any = {
        providerId: 'google.com',
        email: firebaseUser.email,
        linkedAt: new Date()
      };
      
      if (firebaseUser.displayName) {
        newLinkedAccount.displayName = firebaseUser.displayName;
      }
      if (firebaseUser.photoURL) {
        newLinkedAccount.photoURL = firebaseUser.photoURL;
      }
      
      linkedAccounts.push(newLinkedAccount);
      updateData.linkedAccounts = linkedAccounts;
    }

    if (needsProfilePictureUpdate) {
      updateData.profilePicture = firebaseUser.photoURL;
    }
    
    try {
      await updateDoc(doc(db, 'users', existingUserDoc.id), updateData);
      console.log('✅ Existing user document updated successfully');
    } catch (error) {
      console.error('❌ Failed to update existing user document:', error);
      throw error;
    }
  } else {
    console.log('🔍 No updates needed for existing user');
  }

  // Return user data without migration
  userData = { 
    uid: existingUserDoc.id, 
    ...existingUserData, 
    createdAt: (existingUserData.createdAt as any)?.toDate ? (existingUserData.createdAt as any).toDate() : new Date(),
    updatedAt: new Date()
  };
    } else {
      // New user - create account with new rating system and email preferences
      console.log('🔍 Creating new Google user...');
      
      const initialRatings = {
        utr: null,
        ntrp: null
      };

      const defaultEmailPreferences: EmailPreferences = {
        challengeReceived: true,
        challengeAccepted: true,
        challengeRejected: true,
        accountSecurity: true
      };
      
      // Create linkedAccount object with proper null handling
      const linkedAccount: any = {
        providerId: 'google.com',
        email: firebaseUser.email,
        linkedAt: new Date() // Client-side timestamp for linked accounts
      };
      
      // Only add fields if they have actual values (not undefined)
      if (firebaseUser.displayName) {
        linkedAccount.displayName = firebaseUser.displayName;
      }
      if (firebaseUser.photoURL) {
        linkedAccount.photoURL = firebaseUser.photoURL;
      }
      
      const newUserData: any = {
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
        emailVerified: true,
        preferences: {
          level: 'Not Rated',
          ratings: initialRatings,
          preferredCourts: [],
          defaultDuration: 60,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          emailNotifications: true,
          emailPreferences: defaultEmailPreferences
        },
        linkedAccounts: [linkedAccount],
        // Use serverTimestamp() for new users
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Only add profilePicture if it has a value
      if (firebaseUser.photoURL) {
        newUserData.profilePicture = firebaseUser.photoURL;
      }

      console.log('🔥 Creating new Google user document with serverTimestamp:', firebaseUser.uid);

      try {
        // THIS TRIGGERS THE EMAIL AUTOMATICALLY via onUserCreated trigger (but skipped for verified users)
        await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
        console.log('✅ New Google user document created successfully');
      } catch (error) {
        console.error('❌ Failed to create new Google user document:', error);
        throw error;
      }
      
      userData = { 
        uid: firebaseUser.uid, 
        ...newUserData,
        // Convert timestamps for immediate use
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }

    console.log('✅ signInWithGoogle completed successfully');
    return userData;
  }

  public async linkGoogleAccount(): Promise<void> {
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();
    
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }

    const result = await linkWithPopup(auth.currentUser, this.googleProvider);
    const googleUser = result.user;

    if (!googleUser.email) {
      throw new Error('Google account must have an email address');
    }

    // Update user document with linked account
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const linkedAccounts = userData.linkedAccounts || [];
      
      // Check if Google account not already linked
      const googleLinked = linkedAccounts.find((account: LinkedAccount) => account.providerId === 'google.com');
      
      if (!googleLinked) {
        const newLinkedAccount: LinkedAccount = {
          providerId: 'google.com',
          displayName: googleUser.displayName || undefined,
          email: googleUser.email,
          photoURL: googleUser.photoURL || undefined,
          linkedAt: new Date() // This is converted client-side, so new Date() is fine
        };
        
        linkedAccounts.push(newLinkedAccount);
        
        const updateData: any = {
          linkedAccounts,
          updatedAt: serverTimestamp()
        };

        // Only add profilePicture if it has a value and user doesn't already have one
        if (googleUser.photoURL && !userData.profilePicture) {
          updateData.profilePicture = googleUser.photoURL;
        }
        
        await updateDoc(doc(db, 'users', auth.currentUser.uid), updateData);
      }
    }
  }

  public async unlinkProvider(providerId: string): Promise<void> {
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();
    
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }

    // Unlink from Firebase Auth
    await unlink(auth.currentUser, providerId);

    // Update user document
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const linkedAccounts = (userData.linkedAccounts || []).filter(
        (account: LinkedAccount) => account.providerId !== providerId
      );
      
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        linkedAccounts,
        updatedAt: serverTimestamp()
      });
    }
  }

  // Updated to use our secure Firebase Function (SERVER-SIDE ONLY)
  public async sendPasswordReset(email: string, captchaToken?: string): Promise<{ success: boolean; message: string; requiresCaptcha?: boolean }> {
    try {
      const functions = this.firebaseService.getFunctions();
      const sendPasswordResetSecure = httpsCallable(functions, 'sendPasswordResetEmailSecure');

      const result = await sendPasswordResetSecure({
        email: email.toLowerCase().trim(),
        captchaToken: captchaToken
      });

      return result.data as { success: boolean; message: string; requiresCaptcha?: boolean };

    } catch (error: any) {
      console.error('Password reset request failed:', error);
      
      // Handle specific error cases
      if (error.code === 'functions/resource-exhausted') {
        throw new Error('Too many password reset attempts. Please try again later.');
      } else if (error.code === 'functions/failed-precondition') {
        // Need CAPTCHA verification
        return {
          success: false,
          message: 'Additional verification required',
          requiresCaptcha: true
        };
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('Request blocked due to suspicious activity. Please try again later.');
      }
      
      throw new Error('Unable to send password reset email. Please try again.');
    }
  }

  // Verify password reset token (SERVER-SIDE ONLY)
  public async verifyPasswordResetToken(token: string): Promise<{ valid: boolean; userId?: string; email?: string }> {
    try {
      const functions = this.firebaseService.getFunctions();
      const verifyToken = httpsCallable(functions, 'verifyPasswordResetToken');

      const result = await verifyToken({ token });
      return result.data as { valid: boolean; userId?: string; email?: string };

    } catch (error: any) {
      console.error('Token verification failed:', error);
      
      if (error.code === 'functions/invalid-argument') {
        throw new Error('Invalid or expired reset link.');
      } else if (error.code === 'functions/deadline-exceeded') {
        throw new Error('Reset link has expired. Please request a new one.');
      } else if (error.code === 'functions/failed-precondition') {
        throw new Error('This reset link has already been used.');
      }
      
      throw new Error('Unable to verify reset link.');
    }
  }

  // Complete password reset with new password (SERVER-SIDE ONLY)
  public async completePasswordReset(token: string, newPassword: string): Promise<void> {
    try {
      if (newPassword.length < 8) {
        throw new Error('Password must be at least 8 characters long.');
      }

      const functions = this.firebaseService.getFunctions();
      const completeReset = httpsCallable(functions, 'completePasswordReset');

      await completeReset({
        token,
        newPassword
      });

    } catch (error: any) {
      console.error('Password reset completion failed:', error);
      
      if (error.code === 'functions/invalid-argument') {
        if (error.message.includes('8 characters')) {
          throw new Error('Password must be at least 8 characters long.');
        }
        throw new Error('Invalid reset link or password.');
      } else if (error.code === 'functions/failed-precondition') {
        throw new Error('Reset link is invalid or has already been used.');
      }
      
      throw new Error('Unable to reset password. Please try again.');
    }
  }

  // Resend activation email for anonymous users (SERVER-SIDE ONLY)
  public async resendActivationEmail(email: string, captchaToken?: string): Promise<{ success: boolean; message: string; requiresCaptcha?: boolean }> {
    try {
      const functions = this.firebaseService.getFunctions();
      const resendActivation = httpsCallable(functions, 'resendActivationEmailSecure');

      const result = await resendActivation({
        email: email.toLowerCase().trim(),
        captchaToken: captchaToken
      });

      return result.data as { success: boolean; message: string; requiresCaptcha?: boolean };

    } catch (error: any) {
      console.error('Resend activation failed:', error);
      
      if (error.code === 'functions/resource-exhausted') {
        throw new Error('Too many activation email requests. Please try again later.');
      } else if (error.code === 'functions/failed-precondition') {
        // Need CAPTCHA verification
        return {
          success: false,
          message: 'Additional verification required',
          requiresCaptcha: true
        };
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('Request blocked due to suspicious activity. Please try again later.');
      } else if (error.code === 'functions/invalid-argument') {
        if (error.message.includes('already verified')) {
          throw new Error('Your email is already verified. You can sign in normally.');
        }
        throw new Error('Invalid email address or request.');
      }
      
      throw new Error('Unable to resend activation email. Please try again.');
    }
  }

  // Send verification email for authenticated users (SERVER-SIDE ONLY)
  public async sendVerificationEmail(): Promise<void> {
    const auth = this.firebaseService.getAuth();
    
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }

    // Use custom Firebase Function instead of Firebase's built-in verification
    const functions = this.firebaseService.getFunctions();
    const sendCustomVerification = httpsCallable(functions, 'sendCustomEmailVerification');

    try {
      await sendCustomVerification({ uid: auth.currentUser.uid });
    } catch (error: any) {
      console.error('Failed to send verification email:', error);
      
      if (error.code === 'functions/failed-precondition') {
        throw new Error('Email is already verified');
      }
      
      throw new Error('Failed to send verification email. Please try again.');
    }
  }

  // Verify email token
  public async verifyEmailToken(token: string): Promise<{ success: boolean; message: string }> {
    try {
      const functions = this.firebaseService.getFunctions();
      const verifyToken = httpsCallable(functions, 'verifyEmailToken');

      const result = await verifyToken({ token });
      return result.data as { success: boolean; message: string };

    } catch (error: any) {
      console.error('Email token verification failed:', error);
      
      if (error.code === 'functions/invalid-argument') {
        throw new Error('Invalid or expired verification link.');
      } else if (error.code === 'functions/deadline-exceeded') {
        throw new Error('Verification link has expired. Please request a new one.');
      } else if (error.code === 'functions/failed-precondition') {
        throw new Error('This verification link has already been used.');
      } else if (error.code === 'functions/not-found') {
        throw new Error('Verification link not found.');
      }
      
      throw new Error('Unable to verify email. Please try again.');
    }
  }

  public async getUser(uid: string): Promise<User | null> {
    console.log('🔍 getUser called for uid:', uid);
    const db = this.firebaseService.getFirestore();
    const userDoc = await getDoc(doc(db, 'users', uid));

    if (!userDoc.exists()) {
      console.log('❌ User document not found for uid:', uid);
      return null;
    }

    const userData = userDoc.data();
    console.log('📄 getUser found user data:', {
      hasPreferences: !!userData.preferences,
      hasEmailPreferences: !!userData.preferences?.emailPreferences
    });
    
    // Simple fix: provide defaults for missing fields
    const preferences = userData.preferences || {};
    const emailPreferences = preferences.emailPreferences || {
      challengeReceived: true,
      challengeAccepted: true,
      challengeRejected: true,
      accountSecurity: true
    };

    return { 
      uid, 
      ...userData,
      preferences: {
        ...preferences,
        emailPreferences: emailPreferences,
        emailNotifications: preferences.emailNotifications !== false
      }
    } as User;
  }

  public async getPublicProfile(uid: string): Promise<User | null> {
    console.log('🔍 getPublicProfile called for uid:', uid);
    try {
      const db = this.firebaseService.getFirestore();
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (!userDoc.exists()) {
        console.log('❌ Public profile not found for uid:', uid);
        return null;
      }
      
      const userData = userDoc.data();
      console.log('📄 getPublicProfile found user data:', {
        hasPreferences: !!userData.preferences,
        hasEmailPreferences: !!userData.preferences?.emailPreferences
      });
      
      // Simple fix: provide defaults for missing fields
      const preferences = userData.preferences || {};
      const emailPreferences = preferences.emailPreferences || {
        challengeReceived: true,
        challengeAccepted: true,
        challengeRejected: true,
        accountSecurity: true
      };

      return { 
        uid, 
        ...userData,
        preferences: {
          ...preferences,
          emailPreferences: emailPreferences,
          emailNotifications: preferences.emailNotifications !== false
        }
      } as User;
    } catch (error) {
      console.error(`❌ Error fetching public profile for uid ${uid}:`, error);
      return null;
    }
  }

  async signOut(): Promise<void> {
    console.log('🔍 Signing out user');
    const auth = this.firebaseService.getAuth();
    await auth.signOut();
    console.log('✅ User signed out successfully');
  }

  public async getCurrentUser(): Promise<User | null> {
    console.log('🔍 getCurrentUser called');
    const auth = this.firebaseService.getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.log('❌ No current user in Firebase Auth');
      return null;
    }

    console.log('✅ Firebase Auth user found, fetching Firestore data for uid:', user.uid);
    const db = this.firebaseService.getFirestore();
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        console.log('❌ User document not found in Firestore for uid:', user.uid);
        return null;
      }

      const userData = userDoc.data();
      if (!userData) {
        console.log('❌ User document exists but data is null');
        return null;
      }

      console.log('📄 getCurrentUser found user data:', {
        hasPreferences: !!userData.preferences,
        hasEmailPreferences: !!userData.preferences?.emailPreferences,
        emailNotifications: userData.preferences?.emailNotifications
      });

      // Simple fix: provide defaults for missing fields
      const preferences = userData.preferences || {};
      const emailPreferences = preferences.emailPreferences || {
        challengeReceived: true,
        challengeAccepted: true,
        challengeRejected: true,
        accountSecurity: true
      };

      console.log('✅ getCurrentUser returning user data with defaults applied');
      return {
        uid: user.uid,
        email: userData.email,
        name: userData.name,
        profilePicture: userData.profilePicture,
        preferences: {
          ...preferences,
          emailPreferences: emailPreferences,
          emailNotifications: preferences.emailNotifications !== false
        },
        linkedAccounts: userData.linkedAccounts || [],
        emailVerified: userData.emailVerified,
        createdAt: (userData.createdAt as any)?.toDate ? (userData.createdAt as any).toDate() : userData.createdAt,
        updatedAt: (userData.updatedAt as any)?.toDate ? (userData.updatedAt as any).toDate() : userData.updatedAt
      } as User;
      
    } catch (error) {
      console.error('❌ Error in getCurrentUser:', error);
      throw error;
    }
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    const auth = this.firebaseService.getAuth();
    return auth.onAuthStateChanged(async (firebaseUser: any) => {
      if (firebaseUser) {
        const user = await this.getCurrentUser();
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  // Update user profile
  public async updateUserProfile(uid: string, updates: Partial<User>): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const userRef = doc(db, 'users', uid);
    
    // Validate user data before updating
    this.validateUserData(updates);
    
    // Prepare update data with server timestamp
    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    // Remove uid and timestamps from updates if they exist
    delete updateData.uid;
    delete updateData.createdAt;
    
    await updateDoc(userRef, updateData);
  }

  // Update user preferences
  public async updateUserPreferences(uid: string, preferences: Partial<User['preferences']>): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const userRef = doc(db, 'users', uid);
    
    // Get current user data to merge preferences
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const currentUserData = userDoc.data();
    const updatedPreferences = {
      ...currentUserData.preferences,
      ...preferences
    };
    
    await updateDoc(userRef, {
      preferences: updatedPreferences,
      updatedAt: serverTimestamp()
    });
  }

  // Update user ratings
  public async updateUserRatings(uid: string, ratings: PlayerRatings): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const userRef = doc(db, 'users', uid);
    
    await updateDoc(userRef, {
      'preferences.ratings': ratings,
      updatedAt: serverTimestamp()
    });
  }

  // Update email preferences
  public async updateEmailPreferences(uid: string, emailPreferences: EmailPreferences): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const userRef = doc(db, 'users', uid);
    
    await updateDoc(userRef, {
      'preferences.emailPreferences': emailPreferences,
      'preferences.emailNotifications': emailPreferences.challengeReceived || emailPreferences.challengeAccepted || emailPreferences.challengeRejected,
      updatedAt: serverTimestamp()
    });
  }

  // Check if email is available for registration
  public async isEmailAvailable(email: string): Promise<boolean> {
    const db = this.firebaseService.getFirestore();
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase())
    );
    
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.empty;
  }

  // Search users by name or email (for admin functions)
  public async searchUsers(searchTerm: string, limit: number = 20): Promise<User[]> {
    const db = this.firebaseService.getFirestore();
    
    // Note: Firestore doesn't support full-text search natively
    // This is a basic implementation that searches by exact email match
    // For production, consider using Algolia or similar search service
    
    const emailQuery = query(
      collection(db, 'users'),
      where('email', '==', searchTerm.toLowerCase())
    );
    
    const emailSnapshot = await getDocs(emailQuery);
    const users: User[] = [];
    
    emailSnapshot.forEach(doc => {
      const userData = doc.data();
      users.push({
        uid: doc.id,
        ...userData,
        createdAt: (userData.createdAt as any)?.toDate ? (userData.createdAt as any).toDate() : userData.createdAt,
        updatedAt: (userData.updatedAt as any)?.toDate ? (userData.updatedAt as any).toDate() : userData.updatedAt
      } as User);
    });
    
    return users.slice(0, limit);
  }

  // Delete user account (requires admin privileges or user confirmation)
  public async deleteUserAccount(uid: string): Promise<void> {
    const auth = this.firebaseService.getAuth();
    const db = this.firebaseService.getFirestore();
    
    // Verify that the current user is trying to delete their own account
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
      throw new Error('Unauthorized: Can only delete your own account');
    }
    
    try {
      // Delete user document from Firestore
      await updateDoc(doc(db, 'users', uid), {
        deleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Note: Actual Firebase Auth user deletion should be handled by admin functions
      // for security reasons. This just marks the user as deleted in Firestore.
      
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw new Error('Failed to delete account. Please try again.');
    }
  }

  // Refresh user data from Firestore
  public async refreshUserData(uid: string): Promise<User | null> {
    return this.getUser(uid);
  }

  // Check if user has completed profile setup
  public async hasCompletedProfile(uid: string): Promise<boolean> {
    const user = await this.getUser(uid);
    if (!user) return false;
    
    // Check required fields for a complete profile
    return !!(
      user.name && 
      user.email && 
      user.preferences.level &&
      user.preferences.timezone
    );
  }

  // Get user statistics (for profile display)
  public async getUserStats(uid: string): Promise<{
    totalSlots: number;
    totalChallenges: number;
    acceptedChallenges: number;
    completedMatches: number;
  }> {
    const db = this.firebaseService.getFirestore();
    
    try {
      // Get user's slots
      const slotsQuery = query(
        collection(db, 'playerSlots'),
        where('playerId', '==', uid)
      );
      const slotsSnapshot = await getDocs(slotsQuery);
      
      // Get challenges sent by user
      const challengesQuery = query(
        collection(db, 'challenges'),
        where('fromUserId', '==', uid)
      );
      const challengesSnapshot = await getDocs(challengesQuery);
      
      // Get accepted challenges
      const acceptedChallengesQuery = query(
        collection(db, 'challenges'),
        where('fromUserId', '==', uid),
        where('status', '==', 'accepted')
      );
      const acceptedChallengesSnapshot = await getDocs(acceptedChallengesQuery);
      
      // Get completed matches (bookings where user was a player)
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('players', 'array-contains', uid),
        where('status', '==', 'completed')
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      return {
        totalSlots: slotsSnapshot.size,
        totalChallenges: challengesSnapshot.size,
        acceptedChallenges: acceptedChallengesSnapshot.size,
        completedMatches: bookingsSnapshot.size
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      return {
        totalSlots: 0,
        totalChallenges: 0,
        acceptedChallenges: 0,
        completedMatches: 0
      };
    }
  }

  // Update last active timestamp
  public async updateLastActive(uid: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const userRef = doc(db, 'users', uid);
    
    try {
      await updateDoc(userRef, {
        lastActive: serverTimestamp()
      });
    } catch (error) {
      // Don't throw error for last active update failures
      console.warn('Failed to update last active timestamp:', error);
    }
  }

  // Validate user data before saving
  private validateUserData(userData: Partial<User>): boolean {
    if (userData.email && !this.isValidEmail(userData.email)) {
      throw new Error('Invalid email format');
    }
    
    if (userData.name && userData.name.trim().length < 2) {
      throw new Error('Name must be at least 2 characters long');
    }
    
    if (userData.preferences?.defaultDuration && 
        (userData.preferences.defaultDuration < 30 || userData.preferences.defaultDuration > 300)) {
      throw new Error('Default duration must be between 30 and 300 minutes');
    }
    
    return true;
  }

  // Helper method to validate email format
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Helper method to sanitize user input
  private sanitizeUserInput(input: string): string {
    return input.trim().replace(/[<>]/g, '');
  }

  // Batch update multiple users (admin function)
  public async batchUpdateUsers(updates: Array<{ uid: string; data: Partial<User> }>): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    // Process in batches of 500 (Firestore limit)
    const batchSize = 500;
    for (let i = 0; i < updates.length; i += batchSize) {
      const batch = writeBatch(db);
      
      const batchUpdates = updates.slice(i, i + batchSize);
      
      batchUpdates.forEach(update => {
        const userRef = doc(db, 'users', update.uid);
        const updateData = {
          ...update.data,
          updatedAt: serverTimestamp()
        };
        batch.update(userRef, updateData);
      });
      
      await batch.commit();
    }
  }

  // Export user data (GDPR compliance)
  public async exportUserData(uid: string): Promise<any> {
    const auth = this.firebaseService.getAuth();
    
    // Verify that the current user is requesting their own data
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
      throw new Error('Unauthorized: Can only export your own data');
    }
    
    const db = this.firebaseService.getFirestore();
    
    try {
      // Get user profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      // Get user's slots
      const slotsQuery = query(
        collection(db, 'playerSlots'),
        where('playerId', '==', uid)
      );
      const slotsSnapshot = await getDocs(slotsQuery);
      const slots = slotsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get user's challenges
      const challengesQuery = query(
        collection(db, 'challenges'),
        where('fromUserId', '==', uid)
      );
      const challengesSnapshot = await getDocs(challengesQuery);
      const challenges = challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get user's bookings
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('players', 'array-contains', uid)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      return {
        exportDate: new Date().toISOString(),
        user: userData,
        slots,
        challenges,
        bookings
      };
    } catch (error) {
      console.error('Error exporting user data:', error);
      throw new Error('Failed to export user data');
    }
  }
}